import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { PracticeAttendees } from './PracticeAttendees';
import { useTrainingCourses } from '../../../hooks/useTrainingCourses';
import { AttendeesPickerModal } from './AttendeesPickerModal';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { mockData } from '../../../services/mockData';
import { trainingCategories, trainingProducts } from '../../../data/trainingProducts';
import { externalPartners } from '../../../data/practiceOptions';
import { formatEuro, parseAmount } from '../../../utils/currency';
import { practiceTotal, withAttendees, withTotal } from '../../../utils/practices';
import { ChoiceGroup } from '../ChoiceGroup';
import { PracticeRateType, PracticeAttendee, PracticeClientType, PracticeDraft, PracticeErrors } from '../../../types/practice';
interface PracticeMainFieldsProps {
  draft: PracticeDraft;
  onChange: (changes: Partial<PracticeDraft>) => void;
  errors: PracticeErrors;
}
const sectionTitle = 'text-sm font-semibold text-seform-navy mb-4';
export function PracticeMainFields({
  draft,
  onChange,
  errors
}: PracticeMainFieldsProps) {
  const clientOptions = mockData.getClients().map(c => ({
    value: c.name,
    label: c.name
  }));
  const productOptions = trainingProducts.filter(p => p.category === draft.category).map(p => ({
    value: p.code,
    label: `${p.code} — ${p.type} · ${p.name}`
  }));
  const total = practiceTotal(draft);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const courses = useTrainingCourses();
  // Corso richiesto: courses of the chosen product (all courses if none chosen)
  const requestedOptions = courses.filter(c => !draft.productCode || c.productCode === draft.productCode).map(c => ({
    value: c.courseCode,
    label: `${c.courseCode} — ${c.name}`
  }));
  const assignedOptions = courses.map(c => ({
    value: c.courseCode,
    label: `${c.courseCode} — ${c.name}`
  }));
  const assignedCourse = courses.find(c => c.courseCode === draft.courseCode);

  // Picker returns the company's Risorse umane: the other people are kept
  const confirmPicker = (workersList: PracticeAttendee[]) => {
    const others = (draft.attendees || []).filter(a => a.workerId === undefined);
    onChange(withAttendees(draft, [...workersList, ...others]));
  };

  // The three client types are alternatives: exactly one is always selected
  const option = (type: PracticeClientType, label: string, hint: string) => <label className="flex items-start gap-2 cursor-pointer" data-id="element-1615" data-mp="cbnveelcdka1">
      <input type="radio" name="practice-client-type" className="mt-0.5 border-slate-300 text-seform-orange focus:ring-seform-orange" checked={draft.clientType === type} onChange={() => {
      const next = {
        ...draft,
        clientType: type,
        attendees: []
      };
      onChange({
        clientType: type,
        ...withTotal(next, type === 'individuo' ? 1 : draft.participantsCount)
      });
    }} data-id="element-1616" data-mp="7j353n1n9pb9" />
      <span data-id="element-1617" data-mp="1hi5opye7ajyz">
        <span className="block text-sm font-medium text-slate-700" data-id="element-1618" data-mp="1hinpn8bt5md8">{label}</span>
        <span className="block text-xs text-slate-500" data-id="element-1619" data-mp="1li6dabv0kbjr">{hint}</span>
      </span>
    </label>;
  return <div className="divide-y divide-slate-100" data-id="element-1620" data-mp="299lhka6cp2xn">
      <section className="pb-6" data-id="element-1621" data-mp="296pk0myf0g9q">
        <h3 className={sectionTitle} data-id="element-1622" data-mp="2e852c797k8of">Dati principali</h3>
        <div className="space-y-5" data-id="element-1623" data-mp="2e5iwcc1yu18b">
          <fieldset data-id="element-1624" data-mp="208dllyrb4e6q">
            <legend className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1625" data-mp="206srubctgz9x">
              Tipologia cliente
            </legend>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-8" data-id="element-1626" data-mp="2591ll9xik5d3">
              {option('anagrafica', 'Azienda cliente in anagrafica', 'Azienda già presente in anagrafica')}
              {option('individuo', 'Individuo', 'Per privati cittadini')}
              {option('occasionale', 'Azienda Cliente Occasionale', 'Azienda non presente in anagrafica')}
            </div>
          </fieldset>
          {draft.clientType === 'anagrafica' && <SearchableSelect label="Denominazione aziendale" value={draft.companyName || ''} onChange={e => {
          const name = e.target.value;
          if (name === draft.companyName) return;
          // New company: reset attendees and open the selection window
          onChange({
            companyName: name,
            ...withAttendees(draft, [])
          });
          if (name) setIsPickerOpen(true);
        }} options={clientOptions} placeholder="Cerca in anagrafica azienda..." description="Recuperata automaticamente dall'anagrafica azienda" error={errors.companyName} required data-id="element-1627" data-mp="vblbhxcgxlqp" />}
          {draft.clientType === 'individuo' && <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1628" data-mp="2j8kpqzjx4cqg">
              <Input label="Nome" value={draft.firstName || ''} onChange={e => onChange({
            firstName: e.target.value
          })} error={errors.firstName} required data-id="element-1629" data-mp="2n8uw4dbmh55u" />
              <Input label="Cognome" value={draft.lastName || ''} onChange={e => onChange({
            lastName: e.target.value
          })} error={errors.lastName} required data-id="element-1630" data-mp="2wyxz96cgjzhw" />
            </div>}
          {draft.clientType === 'occasionale' && <Input label="Ragione Sociale" value={draft.businessName || ''} onChange={e => onChange({
          businessName: e.target.value
        })} error={errors.businessName} required data-id="element-1631" data-mp="1cjcjq29p1hgr" />}
          <Input label="Email cliente" type="email" value={draft.clientEmail || ''} onChange={e => onChange({
          clientEmail: e.target.value
        })} placeholder="nome@esempio.it" description={draft.clientType === 'anagrafica' ? "Le email vengono inviate anche all'azienda e all'indirizzo Email Formazione in anagrafica" : 'Indirizzo a cui inviare le comunicazioni sul corso'} data-id="element-1632" data-mp="2jw3o9ondhl2n" />
          {(draft.clientType === 'anagrafica' && draft.companyName || draft.clientType === 'occasionale') && <PracticeAttendees draft={draft} onChange={onChange} onOpenPicker={() => setIsPickerOpen(true)} data-id="element-1633" data-mp="37kwf56p512qe" />}
        </div>
        {draft.clientType === 'anagrafica' && draft.companyName && <AttendeesPickerModal isOpen={isPickerOpen} companyName={draft.companyName} selected={draft.attendees || []} onClose={() => setIsPickerOpen(false)} onConfirm={list => {
        confirmPicker(list);
        setIsPickerOpen(false);
      }} data-id="element-1634" data-mp="3gk1cwasvks1i" />}
      </section>

      <section className="py-6" data-id="element-1635" data-mp="3v7rzxglbxk6i">
        <h3 className={sectionTitle} data-id="element-1636" data-mp="3vawaqwtave65">Informazioni corso</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1637" data-mp="3hgde7201f4vr">
          <Select label="Macrocategoria" value={draft.category} onChange={e => onChange({
          category: e.target.value,
          productCode: ''
        })} options={[{
          value: '',
          label: 'Seleziona macrocategoria...'
        }, ...trainingCategories.map(c => ({
          value: c,
          label: c
        }))]} error={errors.category} required data-id="element-1638" data-mp="3m84xqsks0ace" />
          <SearchableSelect label="Codice prodotto + Tipologia corso" value={draft.productCode} onChange={e => {
          const productCode = e.target.value;
          const requested = courses.find(c => c.courseCode === draft.requestedCourseCode);
          onChange({
            productCode,
            // Keep the requested course only if it belongs to the product
            ...(requested && requested.productCode !== productCode ? {
              requestedCourseCode: undefined
            } : {})
          });
        }} options={productOptions} disabled={!draft.category} placeholder={draft.category ? productOptions.length > 0 ? 'Cerca codice o tipologia...' : 'Nessun prodotto in questa macrocategoria' : 'Seleziona prima la macrocategoria'} error={errors.productCode} required data-id="element-1639" data-mp="33gc54z9mcr57" />
          <SearchableSelect label="Corso richiesto" value={draft.requestedCourseCode || ''} onChange={e => onChange({
          requestedCourseCode: e.target.value || undefined
        })} options={requestedOptions} placeholder={requestedOptions.length > 0 ? 'Cerca codice o nome del corso...' : 'Nessun corso per questo prodotto'} description="Dai corsi inseriti in Formazione › Corsi" data-id="element-1640" data-mp="rtyxh8kwjbk2" />
          <div data-id="element-1641" data-mp="d4j7vvx3xc5y">
            <SearchableSelect label="Corso assegnato" value={draft.courseCode || ''} onChange={e => onChange({
            courseCode: e.target.value || undefined
          })} options={assignedOptions} placeholder="Nessun corso assegnato" description={assignedCourse ? undefined : 'Si compila con il corso richiesto quando la pratica è Positivo'} data-id="element-1642" data-mp="d1x0bwa7uwnq" />
            {assignedCourse && <Link to={`/training/corsi/${assignedCourse.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-seform-orange hover:underline mt-1.5" data-id="element-1643" data-mp="1xk59yqx6c7ox">
                Apri corso {assignedCourse.courseCode}
                <ArrowUpRight className="h-3.5 w-3.5" data-id="element-1644" data-mp="27gi0jfxfa5fg" />
              </Link>}
          </div>
        </div>

        <div className="mt-6" data-id="element-1645" data-mp="1etxb4l0ic3ej">
          <ChoiceGroup label="Tariffa (importo senza IVA)" name="practice-rate-type" value={draft.rateType} options={[{
          value: 'oraria',
          label: 'Tariffa oraria'
        }, {
          value: 'singola',
          label: 'Tariffa singola'
        }]} onChange={(v: PracticeRateType) => onChange({
          rateType: v
        })} data-id="element-1646" data-mp="1euyowx8wr3zy" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4" data-id="element-1647" data-mp="w2e9oim4sxem">
          {draft.rateType === 'oraria' ? <>
              <Input label="Tariffa oraria" type="number" min={0} step="0.01" value={draft.hourlyRate ?? ''} onChange={e => onChange({
            hourlyRate: parseAmount(e.target.value)
          })} endContent={<span className="text-xs" data-id="element-1649" data-mp="5l27dzhyihmh">€/h</span>} data-id="element-1648" data-mp="oi3mwe95004o" />
              <Input label="Monte ore corso" type="number" min={0} step="0.5" value={draft.courseHours ?? ''} onChange={e => onChange({
            courseHours: parseAmount(e.target.value)
          })} endContent={<span className="text-xs" data-id="element-1651" data-mp="3i2h7sjn8p7n8">ore</span>} data-id="element-1650" data-mp="5nex4ytyhwa3" />
            </> : <>
              <Input label="Costo singola iscrizione" type="number" min={0} step="0.01" value={draft.singleFee ?? ''} onChange={e => onChange({
            singleFee: parseAmount(e.target.value)
          })} endContent={<span className="text-xs" data-id="element-1653" data-mp="2ugwrkbsbi1za">€</span>} data-id="element-1652" data-mp="3e0lxwqr8tlxu" />
              {/* Read-only: changed only from "Numero complessivo partecipanti" */}
              <div data-id="element-1654" data-mp="3n49foqe4mhup">
                <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1655" data-mp="3n3rb96hb8xbp">
                  Numero corsisti
                </span>
                <div className="rounded-xl border-2 border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-700 tabular-nums" aria-readonly="true" data-id="element-1656" data-mp="3r4ta3m7bf7d5">
                  {draft.participantsCount ?? '—'}
                </div>
                {draft.clientType !== 'individuo' && <p className="text-xs text-slate-500 mt-1.5" data-id="element-1657" data-mp="enxtkob8tft2">
                    Si modifica da "Numero complessivo partecipanti"
                  </p>}
              </div>
            </>}
          <div data-id="element-1658" data-mp="k1xrpldvcp4p">
            <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1659" data-mp="k0d28a6evwdp">
              Importo complessivo
            </span>
            <div className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-seform-navy tabular-nums" data-id="element-1660" data-mp="xux8rrtaured">
              {total !== undefined ? formatEuro(total) : '—'}
            </div>
            <p className="text-xs text-slate-500 mt-1.5" data-id="element-1661" data-mp="54f5aponyo0f">
              {draft.rateType === 'oraria' ? 'Tariffa oraria × monte ore corso' : 'Costo singola iscrizione × numero corsisti'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6" data-id="element-1662" data-mp="a26ydn11og67">
          <Select label="Provenienza" value={draft.source || ''} onChange={e => onChange({
          source: e.target.value || undefined
        })} description='Elenco "Esterni e Partner"' options={[{
          value: '',
          label: 'Seleziona...'
        }, ...externalPartners.map(p => ({
          value: p,
          label: p
        }))]} data-id="element-1663" data-mp="1qh89d2gwosb6" />
          <Input label="Data" type="date" value={draft.date || ''} onChange={e => onChange({
          date: e.target.value || undefined
        })} data-id="element-1664" data-mp="4zpltbg0rc5o" />
        </div>
      </section>
    </div>;
}